import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/Modal';
import { Step } from 'components/SteppedModal';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnMount from 'hooks/useOnMount';
import { projectPodcastNameSelector } from 'redux/modules/download';
import { showError } from 'redux/modules/notification';
import {
  createPlaylistStatusSelector,
  createYoutubePlaylist,
} from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { RequestStatus, YoutubeVideoVisibility } from 'types';
import { YouTubeSocialShareStep } from '../../types';
import YouTubeCreatePlaylistStep from './YouTubeCreatePlaylistStep';

export default function useYouTubeCreatePlaylistStep(): Step<
  YouTubeSocialShareStep
> {
  const podcastName = useSelector(projectPodcastNameSelector);
  const createPlaylistStatus = useSelector(createPlaylistStatusSelector);

  const [visibility, setVisibility] = useState<YoutubeVideoVisibility>(
    'public',
  );
  const [playlistName, setPlaylistName] = useState(podcastName);

  const isCreatingPlaylist = createPlaylistStatus === RequestStatus.REQUEST;

  const { goToStep } = useSocialShareModal();

  const dispatch = useDispatch<Dispatch>();

  const handleCreatePlaylist = useCallback(async () => {
    try {
      await dispatch(createYoutubePlaylist(playlistName, visibility));

      goToStep('composer');
    } catch {
      dispatch(
        showError({
          type: 'help',
          dismissAfterSec: 10,
          title: 'Error creating YouTube playlist',
          message: 'Please try again or {{link}} so we can help',
          actionLabel: 'contact us',
        }),
      );
    }
  }, [dispatch, goToStep, playlistName, visibility]);

  const resetState = useCallback(() => {
    setVisibility('public');
    setPlaylistName(podcastName);
  }, [podcastName]);

  useOnMount(resetState);

  return {
    id: 'create-playlist',
    component: (
      <YouTubeCreatePlaylistStep
        {...{
          isCreatingPlaylist,
          playlistName,
          visibility,
        }}
        onPlaylistNameChange={setPlaylistName}
        onVisibilityChange={setVisibility}
      />
    ),
    fluidFooterButtons: true,
    renderFooterButtons: () => [
      <Modal.FooterButton
        theme="submit-alt"
        disabled={isCreatingPlaylist}
        onClick={() => goToStep('composer')}
      >
        cancel
      </Modal.FooterButton>,
      <Modal.FooterButton
        theme="submit"
        disabled={!playlistName || isCreatingPlaylist}
        onClick={handleCreatePlaylist}
      >
        create playlist
      </Modal.FooterButton>,
    ],
  };
}
